import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { GetProgressionSubjects, submitReRegistrationService, GetAccountPayerDetails, getReRegistrationService } from "../../../services/ReRegistrationService";
import { setReRegistrationsReload } from "../../../store/actions";
import { brand } from "../../../utils/Constants";
import { useDebouncedApiEffect } from "../../../hooks/useDebouncedApiEffect";
import SkeletonPMCLinkedRegForm from "../../../loaders/SkeletonPMCLinkedRegForm";

// Import shared utilities and hooks
import { DeliveryMethodMap, groupModulesByYear, parseIdList, normalizeModuleId, STEP_MODULE_SELECTION, STEP_ACCOUNT_DETAILS, countsTowardsModuleLimit } from "./shared/utils/moduleHelpers";
import { getPmcAutoSelection, countActiveRedoProgressionModules } from "./shared/utils/pmcSelectionHelpers";
import { useModuleSelection } from "./shared/hooks/useModuleSelection";
import { useExpandCollapse, useExpandCollapseList } from "./shared/hooks/useExpandCollapse";

// Import application components
import ApplicationLayout from "./application/layout/ApplicationLayout";
import YearAccordion from "./application/modules/YearAccordion";
import ModuleSummaryCard from "./application/summary/ModuleSummaryCard";
import SubmissionSection from "./application/submission/SubmissionSection";
import { Container , ModuleWrapper} from "./application/styles/primitives";
import { AccountPayerChangeStep } from "./application";
import { SummaryDisclaimerBlock } from "./application/styles/summaryStyles";

function PMCRegistrationApplication(props) {
    const {
        registration,
        setReadOnly,
        setStudentNewRegId,
        readOnly,
        lateReg,
        readOnlyData,
        setCurrentStep,
        setStepsAvailable,
        currentStep,
        stepsAvailable,
        setSubmitted,
        qualificationDetails,
        bachleorData,
        setIsData,
        updateStatus,
        onModuleSummaryChange,
        onAccountPayerComplete,
        setRegistration
    } = props || {};

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const userData = JSON.parse(localStorage.getItem('user'));
    const dispatch = useDispatch();
    const isReReg = useSelector(state => state.isReReg);
    const reload = useSelector(state => state.registrations.reload);
    const [showModules, setShowModules] = useState(null);
    const [isSig, setIsSig] = useState("");
    const signaturePadRef = useRef(null);
    const fileInputRef = useRef(null);
    const [ipAddress, setIpAddress] = useState('');
    const [cDate, setCDate] = useState('');
    const [qualificationName, setQualificationName] = useState(null);
    const [allModules, setAllModules] = useState([]);
    const [maxModules, setMaxModules] = useState(0);
    const [minModules, setMinModules] = useState(0);
    const [internalView, setInternalView] = useState('selection');
    const [isEditMode, setIsEditMode] = useState(false);
    const [preloadedAccountPayer, setPreloadedAccountPayer] = useState(null);
    const [accountPayerLoading, setAccountPayerLoading] = useState(false);
    const [initialAutoSelectedRedo, setInitialAutoSelectedRedo] = useState([]);
    const [initialAutoSelectedProgression, setInitialAutoSelectedProgression] = useState([]);
    const [moduleSummaryData, setModuleSummaryData] = useState(null);

    // Use custom hooks for module selection and expand/collapse
    const {
        selectedRedoSubjects,
        selectedProgressionSubjects,
        creditModulesIds,
        setSelectedRedoSubjects,
        setSelectedProgressionSubjects,
        setCreditModulesIds,
        setAllModulesState,
        handleAddModule,
        handleRemoveModule
    } = useModuleSelection([], [], []);

    const { expandedItems: expandedYears, toggleItem: toggleYearExpanded, isExpanded: isYearExpanded, setExpandedItems: setExpandedYears } = useExpandCollapse({});
    const { expandedList: expandedModules, setExpandedList: setExpandedModules, toggleItem: toggleModuleExpanded } = useExpandCollapseList([]);

    // Count selected modules for max-module guardrail, deduplicated against actual module list
    const countedModuleTotal = (() => {
        const allModulesArray = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
        if (!allModulesArray.length) {
            return (selectedRedoSubjects?.length || 0) + (selectedProgressionSubjects?.length || 0);
        }

        const seenIds = new Set();
        const items = [
            ...(selectedRedoSubjects || []).map(id => ({ id, type: "redo" })),
            ...(selectedProgressionSubjects || []).map(id => ({ id, type: "progression" }))
        ]
            .map(item => {
                const module = allModulesArray.find(
                    (mod) => normalizeModuleId(mod.netsuite_product_id) === normalizeModuleId(item.id)
                );
                if (!module) {
                    return null;
                }
                const normalizedId = normalizeModuleId(module.netsuite_product_id);
                if (seenIds.has(normalizedId)) {
                    return null;
                }
                seenIds.add(normalizedId);
                return {
                    id: normalizedId,
                    type: item.type
                };
            })
            .filter(Boolean);

        return items.filter(item => countsTowardsModuleLimit(item.type)).length;
    })();

    useEffect(() => {
        setInternalView('selection');
    }, [currentStep]);

    // Extract all prerequisites from modules and add them to allModulesState for name-based matching
    useEffect(() => {
        if (!allModules || (Array.isArray(allModules) && allModules.length === 0)) {
            setAllModulesState([]);
            return;
        }

        const allModulesArray = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
        const prerequisitesMap = new Map();
        
        // Extract all prerequisites from all modules
        allModulesArray.forEach(module => {
            if (module.pre_requisites && typeof module.pre_requisites === "object" && module.pre_requisites !== null) {
                Object.entries(module.pre_requisites).forEach(([prereqId, prereq]) => {
                    if (prereq && prereq.name) {
                        // Create a normalized module entry for the prerequisite
                        const prereqModule = {
                            netsuite_product_id: prereqId,
                            name: prereq.name,
                            status: prereq.reason || prereq.status,
                            reason: prereq.reason || prereq.status,
                            term: prereq.term,
                            price: prereq.price || 5000,
                            ...prereq
                        };
                        // Use ID as key to avoid duplicates
                        prerequisitesMap.set(prereqId, prereqModule);
                    }
                });
            }
        });

        // Combine main modules with prerequisites
        const allModulesWithPrereqs = [
            ...allModulesArray,
            ...Array.from(prerequisitesMap.values())
        ];

        setAllModulesState(allModulesWithPrereqs);
    }, [allModules, setAllModulesState]);

    useEffect(() => {
        if (!readOnly && setCurrentStep) {
            setCurrentStep(STEP_MODULE_SELECTION, { skipTabSync: true });
        } else if (readOnly && setCurrentStep && currentStep !== STEP_MODULE_SELECTION && currentStep !== 2) {
            // In read-only mode, if currentStep is not set to step 1 or 2, default to step 1
            setCurrentStep(STEP_MODULE_SELECTION, { skipTabSync: true });
        }
    }, [readOnly, setCurrentStep, currentStep]);

    // Preload account payer details early so they're available when user reaches step 2
    useEffect(() => {
        const userId = userData?.id;
        if (!userId || accountPayerLoading || preloadedAccountPayer !== null) {
            return;
        }

        setAccountPayerLoading(true);
        GetAccountPayerDetails(userId)
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data[0] : res.data || {};
                setPreloadedAccountPayer(data);
            })
            .catch((error) => {
                console.error("Error preloading account payer details:", error);
                setPreloadedAccountPayer(null);
            })
            .finally(() => {
                setAccountPayerLoading(false);
            });
    }, [userData?.id, accountPayerLoading, preloadedAccountPayer]);
    useEffect(() => {
        if (!onModuleSummaryChange) {
            return;
        }

        const allModulesArray = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
        
        const transferModulesArray = (showModules?.transfer?.core_data || []);
        
        const modulesMap = new Map();
        
        allModulesArray.forEach(module => {
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (normalizedId) {
                modulesMap.set(normalizedId, module);
            }
        });
        
        transferModulesArray.forEach(module => {
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (normalizedId) {
                const existingModule = modulesMap.get(normalizedId);
                if (!existingModule || 
                    (module.pre_requisites && Object.keys(module.pre_requisites).length > 0 && 
                     (!existingModule.pre_requisites || Object.keys(existingModule.pre_requisites).length === 0))) {
                    modulesMap.set(normalizedId, module);
                }
            }
        });
        
        const allModulesWithTransfers = Array.from(modulesMap.values());

        if (!allModulesWithTransfers.length) {
            return;
        }

        const getModuleById = (id) => {
            const normalizedId = normalizeModuleId(id);
            return allModulesWithTransfers.find(
                (module) => normalizeModuleId(module.netsuite_product_id) === normalizedId
            );
        };


        const isSubmitted = readOnly || Boolean(registration?.studentNewRegId);
        const effectiveRedoIds = isSubmitted && readOnlyData
            ? parseIdList(readOnlyData.redo || "")
            : selectedRedoSubjects;
        const effectiveProgressionIds = isSubmitted && readOnlyData
            ? parseIdList(readOnlyData.progression || "")
            : selectedProgressionSubjects;
        const effectiveCreditIds = isSubmitted && readOnlyData
            ? parseIdList(readOnlyData.credit_modules || "")
            : creditModulesIds;


        const seenIds = new Set();
        const itemsWithOrder = [];
        
  
        effectiveRedoIds.forEach((id) => {
            itemsWithOrder.push({ id, type: "redo", originalIndex: itemsWithOrder.length });
        });
        
        effectiveProgressionIds.forEach((id) => {
            itemsWithOrder.push({ id, type: "progression", originalIndex: itemsWithOrder.length });
        });
        
        effectiveCreditIds.forEach((id) => {
            itemsWithOrder.push({ id, type: "credit", originalIndex: itemsWithOrder.length });
        });

        // Helper to check if a module is inactive or not eligible (for filtering redo/progression in summary)
        const isInactiveOrNotEligibleForSummary = (module) => {
            if (!module) return false;
            const moduleStatus = (module.module_status || "").toLowerCase().trim();
            const status = (module.status || "").toLowerCase().trim();
            const reason = (module.reason || "").toLowerCase().trim();
            
            const inactivePatterns = ["inactive", "upcoming"];
            const notEligiblePatterns = ["not eligible", "noteligible", "not_eligible", "deregister"];
            
            const checkPatterns = (text, patterns) => patterns.some(p => text.includes(p));
            
            return (
                checkPatterns(moduleStatus, inactivePatterns) ||
                checkPatterns(status, [...inactivePatterns, ...notEligiblePatterns]) ||
                checkPatterns(reason, [...inactivePatterns, ...notEligiblePatterns])
            );
        };

        const items = itemsWithOrder
            .map((item) => {
                const module = getModuleById(item.id);
                if (!module) {
                    return null;
                }
                const normalizedId = normalizeModuleId(module.netsuite_product_id);
                if (seenIds.has(normalizedId)) {
                    return null;
                }
                // For redo and progression types, exclude inactive/not-eligible modules from summary
                // Credit/adjustment modules are always shown (they represent deductions)
                if ((item.type === "redo" || item.type === "progression") && isInactiveOrNotEligibleForSummary(module)) {
                    return null;
                }
                seenIds.add(normalizedId);
                const price = Number(module.price) || 0;
                return {
                    id: normalizedId,
                    name: module.name,
                    type: item.type,
                    price,
                    originalIndex: item.originalIndex
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.originalIndex - b.originalIndex);

        const modulesCountSelected = items.filter(
            (item) => item.type === "redo" || item.type === "progression"
        ).length;

        // Count available active redo/progression modules (filtered for inactive/not-eligible)
        const availableRedoModules = (showModules?.redo?.core_data || []).filter(m => !isInactiveOrNotEligibleForSummary(m));
        const availableProgressionModules = (showModules?.progression?.core_data || []).filter(m => !isInactiveOrNotEligibleForSummary(m));
        const availableActiveModuleCount = availableRedoModules.length + availableProgressionModules.length;

        // Cap max to available active modules if available is less than API max
        const rawMaxModules = maxModules || 0;
        const modulesAllowedMax = (rawMaxModules > 0 && availableActiveModuleCount > 0)
            ? Math.min(rawMaxModules, availableActiveModuleCount)
            : rawMaxModules;

        // Also cap min to not exceed the effective max
        const rawModulesRequiredMin = minModules || 0;
        const modulesRequiredMin = (modulesAllowedMax > 0 && rawModulesRequiredMin > 0)
            ? Math.min(rawModulesRequiredMin, modulesAllowedMax)
            : rawModulesRequiredMin;

        const modulesTotal = items
            .filter((item) => item.type === "redo" || item.type === "progression")
            .reduce((total, item) => total + item.price, 0);

        const adjustmentsTotal = items
            .filter((item) => item.type === "credit")
            .reduce((total, item) => total + item.price, 0);

    
        const totalAmount = modulesTotal - adjustmentsTotal;
        
        
        let depositAmount = 0;
        if (totalAmount <= 0) {
            depositAmount = 0;
        } else if (totalAmount >= 14000) {
            depositAmount = 7000;
        } else {
            depositAmount = Number((totalAmount * 0.5).toFixed(2));
        }
        const responseQualificationType = qualificationDetails?.type || "";
        const deliveryMethodKey = (responseQualificationType || "").toLowerCase();
        const deliveryMethodLabel =
            deliveryMethodKey === "parttimeonline"
                ? "Part-Time, Online"
                : deliveryMethodKey === "fulltimeonline"
                ? "Full-Time, Online"
                : deliveryMethodKey === "full"
                ? "Full-Time"
                : responseQualificationType || "";

        const summaryData = {
            items,
            modulesCountSelected,
            modulesRequiredMin,
            modulesAllowedMax,
            modulesTotal,
            adjustmentsTotal,
            depositAmount,
            totalAmount,
            deliveryMethodLabel,
            qualificationName: qualificationName || ""
        };
        
        setModuleSummaryData(summaryData);
        
        if (onModuleSummaryChange) {
            onModuleSummaryChange(summaryData);
        }
    }, []);
    useEffect(() => {
        if (!onModuleSummaryChange) {
            return;
        }

        const allModulesArray = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
        
        const transferModulesArray = (showModules?.transfer?.core_data || []);
        
        const modulesMap = new Map();
        
        allModulesArray.forEach(module => {
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (normalizedId) {
                modulesMap.set(normalizedId, module);
            }
        });
        
        transferModulesArray.forEach(module => {
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (normalizedId) {
                const existingModule = modulesMap.get(normalizedId);
                if (!existingModule || 
                    (module.pre_requisites && Object.keys(module.pre_requisites).length > 0 && 
                     (!existingModule.pre_requisites || Object.keys(existingModule.pre_requisites).length === 0))) {
                    modulesMap.set(normalizedId, module);
                }
            }
        });
        
        const allModulesWithTransfers = Array.from(modulesMap.values());

        if (!allModulesWithTransfers.length) {
            return;
        }

        const getModuleById = (id) => {
            const normalizedId = normalizeModuleId(id);
            return allModulesWithTransfers.find(
                (module) => normalizeModuleId(module.netsuite_product_id) === normalizedId
            );
        };


        const isSubmitted = readOnly || Boolean(registration?.studentNewRegId);
        const effectiveRedoIds = isSubmitted && readOnlyData
            ? parseIdList(readOnlyData.redo || "")
            : selectedRedoSubjects;
        const effectiveProgressionIds = isSubmitted && readOnlyData
            ? parseIdList(readOnlyData.progression || "")
            : selectedProgressionSubjects;
        const effectiveCreditIds = isSubmitted && readOnlyData
            ? parseIdList(readOnlyData.credit_modules || "")
            : creditModulesIds;


        const seenIds = new Set();
        const itemsWithOrder = [];
        
  
        effectiveRedoIds.forEach((id) => {
            itemsWithOrder.push({ id, type: "redo", originalIndex: itemsWithOrder.length });
        });
        
        effectiveProgressionIds.forEach((id) => {
            itemsWithOrder.push({ id, type: "progression", originalIndex: itemsWithOrder.length });
        });
        
        effectiveCreditIds.forEach((id) => {
            itemsWithOrder.push({ id, type: "credit", originalIndex: itemsWithOrder.length });
        });

        // Helper to check if a module is inactive or not eligible (for filtering redo/progression in summary)
        const isInactiveOrNotEligibleForSummary = (module) => {
            if (!module) return false;
            const moduleStatus = (module.module_status || "").toLowerCase().trim();
            const status = (module.status || "").toLowerCase().trim();
            const reason = (module.reason || "").toLowerCase().trim();
            
            const inactivePatterns = ["inactive", "upcoming"];
            const notEligiblePatterns = ["not eligible", "noteligible", "not_eligible", "deregister"];
            
            const checkPatterns = (text, patterns) => patterns.some(p => text.includes(p));
            
            return (
                checkPatterns(moduleStatus, inactivePatterns) ||
                checkPatterns(status, [...inactivePatterns, ...notEligiblePatterns]) ||
                checkPatterns(reason, [...inactivePatterns, ...notEligiblePatterns])
            );
        };

        const items = itemsWithOrder
            .map((item) => {
                const module = getModuleById(item.id);
                if (!module) {
                    return null;
                }
                const normalizedId = normalizeModuleId(module.netsuite_product_id);
                if (seenIds.has(normalizedId)) {
                    return null;
                }
                // For redo and progression types, exclude inactive/not-eligible modules from summary
                // Credit/adjustment modules are always shown (they represent deductions)
                if ((item.type === "redo" || item.type === "progression") && isInactiveOrNotEligibleForSummary(module)) {
                    return null;
                }
                seenIds.add(normalizedId);
                const price = Number(module.price) || 0;
                return {
                    id: normalizedId,
                    name: module.name,
                    type: item.type,
                    price,
                    originalIndex: item.originalIndex
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.originalIndex - b.originalIndex);

        const modulesCountSelected = items.filter(
            (item) => item.type === "redo" || item.type === "progression"
        ).length;

        // Count available active redo/progression modules (filtered for inactive/not-eligible)
        const availableRedoModules = (showModules?.redo?.core_data || []).filter(m => !isInactiveOrNotEligibleForSummary(m));
        const availableProgressionModules = (showModules?.progression?.core_data || []).filter(m => !isInactiveOrNotEligibleForSummary(m));
        const availableActiveModuleCount = availableRedoModules.length + availableProgressionModules.length;

        // Cap max to available active modules if available is less than API max
        const rawMaxModules = maxModules || 0;
        const modulesAllowedMax = (rawMaxModules > 0 && availableActiveModuleCount > 0)
            ? Math.min(rawMaxModules, availableActiveModuleCount)
            : rawMaxModules;

        // Also cap min to not exceed the effective max
        const rawModulesRequiredMin = minModules || 0;
        const modulesRequiredMin = (modulesAllowedMax > 0 && rawModulesRequiredMin > 0)
            ? Math.min(rawModulesRequiredMin, modulesAllowedMax)
            : rawModulesRequiredMin;

        const modulesTotal = items
            .filter((item) => item.type === "redo" || item.type === "progression")
            .reduce((total, item) => total + item.price, 0);

        const adjustmentsTotal = items
            .filter((item) => item.type === "credit")
            .reduce((total, item) => total + item.price, 0);

    
        const totalAmount = modulesTotal - adjustmentsTotal;
        
        
        let depositAmount = 0;
        // if (readOnly && readOnlyData?.depositAmount !== undefined && readOnlyData?.depositAmount !== null) {
      
        //     depositAmount = Number(readOnlyData.depositAmount) || 0;
        // } else {
            // Calculate deposit when not in read-only mode
            if (totalAmount <= 0) {
                depositAmount = 0;
            } else if (totalAmount >= 14000) {
                depositAmount = 7000;
            } else {
                depositAmount = Number((totalAmount * 0.5).toFixed(2));
            }
        // }
        const responseQualificationType = qualificationDetails?.type || "";
        const deliveryMethodKey = (responseQualificationType || "").toLowerCase();
        const deliveryMethodLabel =
            deliveryMethodKey === "parttimeonline"
                ? "Part-Time, Online"
                : deliveryMethodKey === "fulltimeonline"
                ? "Full-Time, Online"
                : deliveryMethodKey === "full"
                ? "Full-Time"
                : responseQualificationType || "";

        const summaryData = {
            items,
            modulesCountSelected,
            modulesRequiredMin,
            modulesAllowedMax,
            modulesTotal,
            adjustmentsTotal,
            depositAmount,
            totalAmount,
            deliveryMethodLabel,
            qualificationName: qualificationName || ""
        };
        
        setModuleSummaryData(summaryData);
        
        if (onModuleSummaryChange) {
            onModuleSummaryChange(summaryData);
        }
    }, [
        allModules,
        showModules?.transfer?.core_data,
        selectedRedoSubjects,
        selectedProgressionSubjects,
        creditModulesIds,
        minModules,
        maxModules,
        qualificationName,
        readOnly,
        readOnlyData,
        qualificationDetails,
        onModuleSummaryChange,
        registration
    ]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            redo: selectedRedoSubjects || [],
            transfer: [],
            credit: [],
            progression: selectedProgressionSubjects || [],
            term_condn: false,
            ip: ipAddress,
            qual_name: qualificationName || "",
            delivery_methode: qualificationDetails?.type || "",
            campus: qualificationDetails?.campus || "",
            student_signature: isSig,
            signature_date: parseInt(new Date(cDate).getTime() / 1000),
            latest_enrolment_netsuite_id: bachleorData?.netsuite_id,
            rereg_academy: registration?.academy,
            rereg_id: registration?.id,
            late_reg: lateReg,
            brand: brand
        },
        validationSchema: Yup.object({
            student_signature: Yup.mixed().required('Signature is required'),
            term_condn: Yup.bool().oneOf([true], "Accept Terms & Conditions is required"),
        }),
        onSubmit: (values, { setErrors }) => {
            const allModulesArrayForValidation = Array.isArray(allModules) ? allModules : Object.values(allModules || {});
            const seenIdsForValidation = new Set();
            const validationItems = [
                ...selectedRedoSubjects.map((id) => ({ id, type: "redo" })),
                ...selectedProgressionSubjects.map((id) => ({ id, type: "progression" }))
            ]
                .map((item) => {
                    const module = allModulesArrayForValidation.find(
                        (mod) => normalizeModuleId(mod.netsuite_product_id) === normalizeModuleId(item.id)
                    );
                    if (!module) {
                        return null;
                    }
                    const normalizedId = normalizeModuleId(module.netsuite_product_id);
                    if (seenIdsForValidation.has(normalizedId)) {
                        return null;
                    }
                    seenIdsForValidation.add(normalizedId);
                    return {
                        id: normalizedId,
                        type: item.type
                    };
                })
                .filter(Boolean);

            const modulesCountSelected = validationItems.filter(
                (item) => countsTowardsModuleLimit(item.type)
            ).length;

            const availableRedoModules = showModules?.redo?.core_data || [];
            const availableProgressionModules = showModules?.progression?.core_data || [];
            const availableActiveModuleCount = countActiveRedoProgressionModules(
                availableRedoModules,
                availableProgressionModules
            );

            const effectiveMaxModules = maxModules || 0;
            let effectiveMinModules = minModules || 0;
            if (availableActiveModuleCount > 0 && effectiveMinModules > 0) {
                effectiveMinModules = Math.min(effectiveMinModules, availableActiveModuleCount);
            }
            // Never validate against an impossible range (e.g. min=2, max=1)
            if (effectiveMaxModules > 0 && effectiveMinModules > 0) {
                effectiveMinModules = Math.min(effectiveMinModules, effectiveMaxModules);
            }

            if (effectiveMinModules > 0 && modulesCountSelected < effectiveMinModules) {
                if (effectiveMaxModules > 0 && effectiveMinModules !== effectiveMaxModules) {
                    setErrors({
                        form: `You must be enrolled for at least ${effectiveMinModules} modules, and not more than ${effectiveMaxModules} modules in total for the next semester`
                    });
                } else if (effectiveMaxModules > 0 && effectiveMinModules === effectiveMaxModules) {
                    setErrors({
                        form: `You must be enrolled for a total of ${effectiveMinModules} modules in total for the next semester`
                    });
                } else {
                    setErrors({
                        form: `You must be enrolled for at least ${effectiveMinModules} modules in total for the next semester`
                    });
                }
                return;
            }
            if (effectiveMaxModules > 0 && modulesCountSelected > effectiveMaxModules) {
                if (effectiveMinModules > 0 && effectiveMinModules !== effectiveMaxModules) {
                    setErrors({
                        form: `You must be enrolled for at least ${effectiveMinModules} modules, and not more than ${effectiveMaxModules} modules in total for the next semester`
                    });
                } else if (effectiveMinModules > 0 && effectiveMinModules === effectiveMaxModules) {
                    setErrors({
                        form: `You must be enrolled for a total of ${effectiveMaxModules} modules in total for the next semester`
                    });
                } else {
                    setErrors({
                        form: `You cannot be enrolled for more than ${effectiveMaxModules} modules in total for the next semester`
                    });
                }
                return;
            }

            const uniqueRedoIds = Array.from(
                new Set((selectedRedoSubjects || []).map((id) => normalizeModuleId(id)))
            );
            const uniqueProgressionIds = Array.from(
                new Set((selectedProgressionSubjects || []).map((id) => normalizeModuleId(id)))
            );
            const uniqueCreditIds = Array.from(
                new Set((creditModulesIds || []).map((id) => normalizeModuleId(id)))
            );

            Swal.fire({ 
                title: "Submitting Re-registration", 
                text: "Please wait while we submit your re-registration module selections, please do not close this window or refresh your page.",
                allowEscapeKey: false, 
                allowOutsideClick: false, 
                onOpen: () => { 
                    Swal.showLoading(); 
                }, 
            });
            setLoading(true);
            const formData = new FormData();
            formData.append('redo', uniqueRedoIds);
            formData.append('transfer', []);
            formData.append('credit', uniqueCreditIds);
            formData.append('progression', uniqueProgressionIds);
            formData.append('student_signature', values?.student_signature);
            formData.append('signature_date', values?.signature_date);
            formData.append('latest_enrolment_netsuite_id', values?.latest_enrolment_netsuite_id);
            formData.append('rereg_academy', values?.rereg_academy);
            formData.append('rereg_id', values?.rereg_id);
            formData.append('next_year_option', values?.next_year_option);
            formData.append('late_reg', values?.late_reg);
            formData.append('brand', values?.brand);
            formData.append('ip', values?.ip);
            formData.append('term_condn', values?.term_condn);
            formData.append('is_bachelor_form', 1);
            formData.append('delivery_methode', values?.delivery_methode);
            formData.append('campus', values?.campus);
            formData.append('qual_name', values?.qual_name);
            submitReRegistrationService(formData)
                .then((res) => {
                    Swal.close();
                    Swal.fire({
                        icon: "success",
                        title: "Re-Registration Successfully Submitted",
                        text: "Your re-registration request has been received. We will be in touch to finalise the process and confirm any next steps.",
                        confirmButtonText: "OK",
                        allowEnterKey: true,
                    }).then(function () {
                        setLoading(false);
                        setStudentNewRegId(res.data.studentNewRegId);
                        setSubmitted(true);
                        setReadOnly(true);
                        setStepsAvailable([STEP_MODULE_SELECTION, 2]);
                        if (setCurrentStep) {
                            setCurrentStep(STEP_ACCOUNT_DETAILS);
                        }
                        updateStatus();
                        if (isReReg.isReReg === true) {
                            dispatch(setReRegistrationsReload(!reload));
                        }
                        // Refresh registration to get updated studentNewRegId for step 2
                        if (registration?.id && setRegistration) {
                            getReRegistrationService(registration.id)
                                .then((refreshRes) => {
                                    if (refreshRes?.data) {
                                        setRegistration(refreshRes.data);
                                    }
                                })
                                .catch((err) => {
                                    console.error("Error refreshing registration:", err);
                                });
                        }
                    });
                })
                .catch((error) => {
                    setLoading(false);
                    Swal.close();
                    const errorCode =
                    error?.code ||
                    error?.status ||
                    error?.response?.status ||
                    null;
                  
                  const title = errorCode
                    ? `Submission Failed (Error ${errorCode})`
                    : "Submission Failed";
                  
                  const baseMessage =
                    error?.message || "An error occurred during submission.";
                  
                  const supportNote = errorCode
                    ? ` Please contact Student Support and reference error code: ${errorCode}.`
                    : " Please contact Student Support and reference this error if it continues.";
                    Swal.fire({
                        icon: "error",
                        title: title,
                        text: baseMessage + supportNote
                    });
                });
        },
    });

    const fetchProgressionSubjects = async ({ cancelToken }) => {
        const data = { "studentId": userData.id, "netsuiteProductId": bachleorData?.netsuite_id };
        setLoadingData(true);
        let availableStepsTemp = [STEP_MODULE_SELECTION, STEP_ACCOUNT_DETAILS];

        try {
            const res = await GetProgressionSubjects(data, { cancelToken });
            if (res?.data?.responseData) {
                const responseData = res?.data?.responseData;
                setQualificationName(responseData?.qualification?.name || '');
                setAllModules(responseData?.AllModules || []);
                
                // Update delivery_methode from GetProgressionSubjects response
                if (responseData?.qualification?.type && formik) {
                    formik.setFieldValue('delivery_methode', responseData.qualification.type);
                }
                
                let deliveryMethodMapped = DeliveryMethodMap[responseData?.qualification?.type] ?? 1;
                let max = deliveryMethodMapped === 1 ? responseData?.qualification?.FTMax ?? 0 : responseData?.qualification?.PTMax ?? 0;
                
                // Helper to check if a module is inactive or not eligible (same filtering as summary)
                const isModuleUnavailable = (module) => {
                    if (!module) return true;
                    const moduleStatus = (module.module_status || "").toLowerCase().trim();
                    const status = (module.status || "").toLowerCase().trim();
                    const reason = (module.reason || "").toLowerCase().trim();
                    
                    const inactivePatterns = ["inactive", "upcoming"];
                    const notEligiblePatterns = ["not eligible", "noteligible", "not_eligible", "deregister"];
                    
                    const checkPatterns = (text, patterns) => patterns.some(p => text.includes(p));
                    
                    return (
                        checkPatterns(moduleStatus, inactivePatterns) ||
                        checkPatterns(status, [...inactivePatterns, ...notEligiblePatterns]) ||
                        checkPatterns(reason, [...inactivePatterns, ...notEligiblePatterns])
                    );
                };

                // Count available modules after filtering out inactive/upcoming/not-eligible
                const availableRedoModules = (responseData?.redoModules || []).filter(m => !isModuleUnavailable(m));
                const availableProgressionModules = (responseData?.progression || []).filter(m => !isModuleUnavailable(m));
                const availableModuleCount = availableRedoModules.length + availableProgressionModules.length;

                // Cap max to available modules if available is less than API max
                const cappedMax = (max > 0 && availableModuleCount > 0)
                    ? Math.min(max, availableModuleCount)
                    : (availableModuleCount > 0 ? availableModuleCount : max);
                setMaxModules(cappedMax);

                let min = deliveryMethodMapped === 1 ? responseData?.qualification?.FTMin ?? 0 : responseData?.qualification?.PTMin ?? 0;
                const cappedMin = cappedMax > 0 ? Math.min(min, cappedMax) : min;
                setMinModules(cappedMin);

                if (responseData?.transferModules) {
                    // Filter out transfer modules that are also enrolled
                    let modulesData = responseData?.transferModules.filter(module => {
                        const status = (module.status || "").toLowerCase().trim();
                        const reason = (module.reason || "").toLowerCase().trim();
                        return status !== "enrolled" && reason !== "enrolled";
                    });
                    if (!readOnly) {
                        setCreditModulesIds(modulesData.filter((module) => module.optional === 0).map((module) => module.netsuite_product_id));
                    }
                }

                // Auto-select modules for PMC bachelor re-registration only:
                const hasExistingSelection =
                    !!(readOnlyData?.redo || readOnlyData?.progression || readOnlyData?.credit_modules);

                if (!readOnly) {
                    const { selectedRedo, selectedProgression } = getPmcAutoSelection({
                        redoModules: responseData?.redoModules || [],
                        progressionModules: responseData?.progression || [],
                        maxModules: max,
                        hasExistingSelection,
                        readOnly
                    });

                    if (!hasExistingSelection) {
                        setSelectedRedoSubjects(selectedRedo);
                        setSelectedProgressionSubjects(selectedProgression);
                        // Store initial auto-selection for comparison
                        setInitialAutoSelectedRedo(selectedRedo);
                        setInitialAutoSelectedProgression(selectedProgression);
                    }
                }

                // Initialize expanded years
                // Filter out transfer modules that are also enrolled
                const filteredTransferModulesForYears = (responseData?.transferModules || []).filter(module => {
                    const status = (module.status || "").toLowerCase().trim();
                    const reason = (module.reason || "").toLowerCase().trim();
                    return status !== "enrolled" && reason !== "enrolled";
                });
                const years = {};
                [...(responseData?.redoModules || []), ...(responseData?.progression || []), ...filteredTransferModulesForYears].forEach(m => {
                    const year = m.year || "First";
                    years[year] = true;
                });
                setExpandedYears(years);
            }

            let updates = {
                redo: { core: false, core_data: [] },
                progression: { core: false, core_data: [] },
                transfer: { core: false, core_data: [] },
                all: []
            };

            if (res?.data?.responseData?.redoModules?.length > 0) {
                updates.redo = { ...updates.redo, core: true };
                res.data.responseData.redoModules.forEach(module => {
                    updates.redo.core_data = [...updates.redo.core_data, module];
                    updates.all = [...updates.all, module];
                });
            }

            if (res?.data?.responseData?.transferModules?.length > 0) {
                // Filter out transfer modules that are also enrolled
                const transferModules = res.data.responseData.transferModules;
                const enrolledTransferModules = transferModules.filter(module => {
                    const status = (module.status || "").toLowerCase().trim();
                    const reason = (module.reason || "").toLowerCase().trim();
                    return status === "enrolled" || reason === "enrolled";
                });
                const excludedCount = enrolledTransferModules.length;
                
                if (excludedCount > 0) {
                    console.log(`Excluded ${excludedCount} transfer module(s) that are also enrolled from re-registration list`);
                }
                
                const filteredTransferModules = transferModules.filter(module => {
                    const status = (module.status || "").toLowerCase().trim();
                    const reason = (module.reason || "").toLowerCase().trim();
                    return status !== "enrolled" && reason !== "enrolled";
                });
                
                if (filteredTransferModules.length > 0) {
                    updates.transfer = { ...updates.transfer, core: true };
                    filteredTransferModules.forEach(module => {
                        updates.transfer.core_data = [...updates.transfer.core_data, module];
                        updates.all = [...updates.all, module];
                    });
                }
            }

            if (res?.data?.responseData?.progression?.length > 0) {
                updates.progression = { ...updates.progression, core: true };
                res.data.responseData.progression.forEach(module => {
                    updates.progression.core_data = [...updates.progression.core_data, module];
                    updates.all = [...updates.all, module];
                });
            }

            setIsData(updates.redo.core || updates.transfer.core || updates.progression.core);
            availableStepsTemp.sort((a, b) => a - b);
            setStepsAvailable(availableStepsTemp);
            setShowModules(prevState => ({ ...prevState, ...updates }));
        } catch (err) {
            if (!axios.isCancel(err)) {
                setIsData(false);
            }
        } finally {
            setLoadingData(false);
        }
    };

    useDebouncedApiEffect(fetchProgressionSubjects, [readOnly, bachleorData?.id, bachleorData?.netsuite_id, bachleorData?.pmc_id, readOnlyData]);

    const handleEditSelection = () => {
        setIsEditMode(!isEditMode);
    };

    const handleDoneEditing = () => {
        setIsEditMode(false);
        setExpandedModules([]);
    };

    const renderModuleSelectionView = () => {
        if (loadingData) {
            return <SkeletonPMCLinkedRegForm />;
        }

        const redoModuleIds = (showModules?.redo?.core_data || []).map(m => m.netsuite_product_id);
        const progressionModuleIds = (showModules?.progression?.core_data || []).map(m => m.netsuite_product_id);
        const transferModuleIds = (showModules?.transfer?.core_data || []).map(m => m.netsuite_product_id);

        const allModulesArray = Array.isArray(allModules) ? allModules : Object.values(allModules || {});

        // Build a set of selected module ids, used to show only selected modules in review mode
        const selectedIdsForFilter = (() => {
            if (readOnly) {
                const readOnlyRedoIds = parseIdList(readOnlyData?.redo);
                const readOnlyProgressionIds = parseIdList(readOnlyData?.progression);
                const readOnlyCreditIds = parseIdList(readOnlyData?.credit_modules);
                const combinedIds = [
                    ...(readOnlyRedoIds || []),
                    ...(readOnlyProgressionIds || []),
                    ...(readOnlyCreditIds || [])
                ];
                return new Set(combinedIds.map(id => normalizeModuleId(id)));
            }
            const combinedIds = [
                ...(selectedRedoSubjects || []),
                ...(selectedProgressionSubjects || []),
                ...(creditModulesIds || [])
            ];
            return new Set(combinedIds.map(id => normalizeModuleId(id)));
        })();

        const transferModulesArray = (showModules?.transfer?.core_data || []);
        
        const modulesMap = new Map();
        
        allModulesArray.forEach(module => {
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (normalizedId) {
                modulesMap.set(normalizedId, module);
            }
        });
        
        transferModulesArray.forEach(module => {
            const normalizedId = normalizeModuleId(module.netsuite_product_id);
            if (normalizedId) {
                const existingModule = modulesMap.get(normalizedId);
                // Prefer transfer module if it exists, or if existing doesn't have prerequisites but transfer does
                if (!existingModule || 
                    (module.pre_requisites && Object.keys(module.pre_requisites).length > 0 && 
                     (!existingModule.pre_requisites || Object.keys(existingModule.pre_requisites).length === 0))) {
                    modulesMap.set(normalizedId, module);
                }
            }
        });
        
        const allModulesWithTransfers = Array.from(modulesMap.values());

        const readOnlyCreditIds = readOnly ? parseIdList(readOnlyData?.credit_modules) : [];

        const allDisplayModules = allModulesWithTransfers
            .filter(m => {
                const moduleId = m.netsuite_product_id;
                const normalizedId = normalizeModuleId(moduleId);
                
                // Filter out inactive modules from module selection accordion
                const isInactive = (m.module_status || "").toLowerCase() === "inactive";
                const isEnrolled =
                    String(m.status || "").trim().toLowerCase() === "enrolled" ||
                    String(m.reason || "").trim().toLowerCase() === "enrolled";
                if (isInactive && !isEnrolled) {
                    return false;
                }
                
                if (readOnly && readOnlyCreditIds.some(id => normalizeModuleId(id) === normalizedId)) {
                    return selectedIdsForFilter.has(normalizedId);
                }

                const inSources =
                    redoModuleIds.includes(moduleId) ||
                    progressionModuleIds.includes(moduleId) ||
                    transferModuleIds.includes(moduleId) ||
                    isEnrolled;

                if (!inSources) {
                    return false;
                }

                // Outside of edit mode (and in read-only), only show selected modules in the table
                if (readOnly || !isEditMode) {
                    return selectedIdsForFilter.has(normalizedId) || isEnrolled;
                }

                // In edit mode, show all available modules from the core lists so user can change selection
                return true;
            })
            .map(m => {
                const moduleId = m.netsuite_product_id;
                const normalizedId = normalizeModuleId(moduleId);
                let moduleSource = "progression";
                
                if (readOnly && readOnlyCreditIds.some(id => normalizeModuleId(id) === normalizedId)) {
                    moduleSource = "transfer";
                } else if (redoModuleIds.includes(moduleId)) {
                    moduleSource = "redo";
                } else if (progressionModuleIds.includes(moduleId)) {
                    moduleSource = "progression";
                } else if (transferModuleIds.includes(moduleId)) {
                    moduleSource = "transfer";
                }
                return { ...m, moduleSource };
            });

        const groupedModules = groupModulesByYear(allDisplayModules);

        // Create enriched allModules array that includes prerequisites for name-based matching
        const enrichedAllModules = (() => {
            const prerequisitesMap = new Map();
            allModulesArray.forEach(module => {
                if (module.pre_requisites && typeof module.pre_requisites === "object" && module.pre_requisites !== null) {
                    Object.entries(module.pre_requisites).forEach(([prereqId, prereq]) => {
                        if (prereq && prereq.name) {
                            const prereqModule = {
                                netsuite_product_id: prereqId,
                                name: prereq.name,
                                status: prereq.reason || prereq.status,
                                reason: prereq.reason || prereq.status,
                                term: prereq.term,
                                price: prereq.price || 5000,
                                ...prereq
                            };
                            prerequisitesMap.set(prereqId, prereqModule);
                        }
                    });
                }
            });
            return [
                ...allModulesArray,
                ...Array.from(prerequisitesMap.values())
            ];
        })();

        const isSelectionDisabled = maxModules > 0 && countedModuleTotal >= maxModules;

        // Get effective module IDs for read-only mode
        const effectiveRedoIds = readOnly 
            ? parseIdList(readOnlyData?.redo) 
            : selectedRedoSubjects;
        const effectiveProgressionIds = readOnly 
            ? parseIdList(readOnlyData?.progression) 
            : selectedProgressionSubjects;
        const effectiveCreditIds = readOnly 
            ? parseIdList(readOnlyData?.credit_modules) 
            : creditModulesIds;

        // Check if user has edited the selection by comparing current selection with initial auto-selection
        const hasEditedSelection = (() => {
            if (readOnly) return false;
            const currentRedoNormalized = (selectedRedoSubjects || []).map(id => normalizeModuleId(id)).sort();
            const currentProgressionNormalized = (selectedProgressionSubjects || []).map(id => normalizeModuleId(id)).sort();
            const initialRedoNormalized = (initialAutoSelectedRedo || []).map(id => normalizeModuleId(id)).sort();
            const initialProgressionNormalized = (initialAutoSelectedProgression || []).map(id => normalizeModuleId(id)).sort();
            
            const redoChanged = JSON.stringify(currentRedoNormalized) !== JSON.stringify(initialRedoNormalized);
            const progressionChanged = JSON.stringify(currentProgressionNormalized) !== JSON.stringify(initialProgressionNormalized);
            
            return redoChanged || progressionChanged;
        })();

        const descriptionText = readOnly
            ? "The modules shown below are your selected modules for this re-registration."
            : hasEditedSelection
            ? "You have customized your module selection. Review your selected modules below."
            : (
                <>
                    The modules shown below were automatically selected for you. If you don't want to use the default selection, click <span style={{ fontWeight: 600 }}>"Edit Selection"</span> to choose your own modules.
                               <SummaryDisclaimerBlock>
                    <i className="fas fa-exclamation-circle"/>
                    <p>
                    Prices shown are an estimate and may not indicate the final price.
                    </p>
                       
                    </SummaryDisclaimerBlock>
                </>
            );

        return (
            <ApplicationLayout
                title="Review Module Selection"
                description={descriptionText}
                sidebarContent={
                        <ModuleSummaryCard
                        selectedRedoModules={effectiveRedoIds}
                        selectedProgressionModules={effectiveProgressionIds}
                        selectedCreditModules={effectiveCreditIds}
                        allModules={allModulesWithTransfers}
                        minModules={minModules}
                        maxModules={maxModules}
                        onEditSelection={readOnly ? undefined : handleEditSelection}
                        onDoneEditing={readOnly ? undefined : handleDoneEditing}
                        onRemoveModule={handleRemoveModule}
                        isEditMode={isEditMode}
                        readOnly={readOnly}
                        items={moduleSummaryData?.items || []}
                        modulesCountSelected={moduleSummaryData?.modulesCountSelected || 0}
                        modulesRequiredMin={moduleSummaryData?.modulesRequiredMin || 0}
                        modulesAllowedMax={moduleSummaryData?.modulesAllowedMax || 0}
                        modulesTotal={moduleSummaryData?.modulesTotal || 0}
                        adjustmentsTotal={moduleSummaryData?.adjustmentsTotal || 0}
                        depositAmount={moduleSummaryData?.depositAmount || 0}
                        totalAmount={moduleSummaryData?.totalAmount || 0}
                        deliveryMethodLabel={moduleSummaryData?.deliveryMethodLabel}
                        submitted={readOnly || Boolean(registration?.studentNewRegId)}
                        />
                }
            >   
            <ModuleWrapper>
                {Object.keys(groupedModules).sort().map(year => (
                    <YearAccordion
                        key={year}
                        year={year}
                        modules={groupedModules[year]}
                        isExpanded={isYearExpanded(year)}
                        onToggle={() => toggleYearExpanded(year)}
                        selectedModules={[
                            ...(effectiveRedoIds || []),
                            ...(effectiveProgressionIds || []),
                            ...(effectiveCreditIds || [])
                        ]}
                        isEditMode={isEditMode}
                        expandedModules={expandedModules}
                        onToggleModuleExpand={toggleModuleExpanded}
                        onRemoveModule={handleRemoveModule}
                        onAddModule={handleAddModule}
                        isSelectionDisabled={isSelectionDisabled}
                        allModules={enrichedAllModules}
                        readOnly={readOnly}
                    />
                ))}

                {Object.keys(groupedModules).length === 0 && (
                    <div style={{ padding: "20px", textAlign: "center", color: "var(--lightGray3)" }}>
                        <i className="fa-light fa-info-circle" style={{ marginRight: "8px" }}></i>
                        No modules available for selection at this time.
                    </div>
                )}
                </ModuleWrapper>
                {!isEditMode ?
                <>
                <SubmissionSection
                    formik={formik}
                    signaturePadRef={signaturePadRef}
                    fileInputRef={fileInputRef}
                    cDate={cDate}
                    ipAddress={ipAddress}
                    setIpAddress={setIpAddress}
                    setCDate={setCDate}
                    setIsSig={setIsSig}
                    readOnly={readOnly}
                    
                    readOnlyData={readOnlyData}
                    registration={registration}
                    loading={loading}
                />

                {Object.values(formik.errors).length > 0 && (
                    <div className="invalid-feedback d-block">
                        {Object.values(formik.errors).map((err, idx) => (<div key={idx}>{err}</div>))}
                    </div>
                )}
                </> : ""}
            </ApplicationLayout>
        );
    };

    const handleAccountPayerSubmitted = ({ updated }) => {
        if (!updated) {
            return;
        }

        if (typeof onAccountPayerComplete === "function") {
            onAccountPayerComplete();
        }


        if (setCurrentStep) {
            setCurrentStep(STEP_MODULE_SELECTION);
        }
    };

    const renderAccountPayerView = () => {
        const studentRegistrationId = registration?.studentNewRegId || "";
        const registrationId = registration?.id || null;
        const sponsorPaymentMethod = registration?.sponsor_payment_method || null;
        const accountPayerUpdated = Number(registration?.account_payer_updated || 0);
        
        return (
            <AccountPayerChangeStep
                onSubmitted={handleAccountPayerSubmitted}
                locked={readOnly}
                preloadedAccountPayer={preloadedAccountPayer}
                studentRegistrationId={studentRegistrationId}
                sponsorPaymentMethod={sponsorPaymentMethod}
                registrationId={registrationId}
                accountPayerUpdated={accountPayerUpdated}
            />
        );
    };

    // Default to step 1 (module selection) if currentStep is not set or invalid
    const effectiveStep = currentStep === STEP_MODULE_SELECTION || currentStep === 2 ? currentStep : STEP_MODULE_SELECTION;

    return (
        <form onSubmit={formik.handleSubmit}>
            <div >
                <div className="theme-form application-form">
                    {effectiveStep === STEP_MODULE_SELECTION && (
                        <>
                            {renderModuleSelectionView()}
                        </>
                    )}
                    {effectiveStep === 2 && renderAccountPayerView()}
                </div>
            </div>
        </form>
    );
}

export default PMCRegistrationApplication;
